import { dueStatusLabels, saleTermModeLabels, type DueStatus } from "@car/api/sale-dues";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@car/ui/components/accordion";
import { Alert, AlertDescription, AlertTitle } from "@car/ui/components/alert";
import { Badge } from "@car/ui/components/badge";
import { Card, CardContent } from "@car/ui/components/card";
import { Image } from "@car/ui/components/image";
import { Progress } from "@car/ui/components/progress";
import { Skeleton } from "@car/ui/components/skeleton";
import { cn, SEP } from "@car/ui/lib/utils";
import {
  Alert01Icon,
  Attachment01Icon,
  Calendar03Icon,
  CheckmarkCircle02Icon,
  DeliveryTruck01Icon,
  Folder01Icon,
  MoneyReceive02Icon,
  PauseIcon,
  PlayIcon,
} from "@hugeicons-pro/core-stroke-rounded";
import { HugeiconsIcon } from "@hugeicons/react";
import { useQuery } from "@tanstack/react-query";
import { Link, createFileRoute, useLocation } from "@tanstack/react-router";
import { useEffect, useRef, type ReactNode } from "react";

import { DeliveryMinutesCard } from "@/components/delivery-minutes";
import { OrderDocuments, PageHeader, PanelEmpty, PanelQueryError } from "@/components/panel-bits";
import { TargetPayments } from "@/components/target-payments";
import { faDate, faDateTimeShort, faNumber, faPercent, faPlainNumber, faToman } from "@/lib/format";
import {
  orderStageKeys,
  orderStageLabels,
  type PanelOverview,
  type TrackedOrder,
} from "@/lib/panel";
import { PayDialog } from "@/routes/pay/-pay-dialog";
import { orpc } from "@/utils/orpc";

export const Route = createFileRoute("/panel/orders")({
  head: () => ({ meta: [{ title: "سفارش‌های در راه | پنل مشتریان تابان خودرو" }] }),
  component: OrdersPage,
});

function OrdersPage() {
  const query = useQuery(orpc.panel.orders.list.queryOptions());
  // a link from a vehicle page (#order-…) lands on its card once the list has loaded
  const hash = useLocation({ select: (location) => location.hash });
  const loaded = Boolean(query.data);
  useEffect(() => {
    if (loaded && hash) document.getElementById(hash)?.scrollIntoView({ block: "start" });
  }, [loaded, hash]);

  return (
    <div>
      <PageHeader
        title="سفارش‌های در راه"
        description="خودروهایی که سفارش داده‌اید و هنوز تحویل نشده‌اند؛ مرحله‌به‌مرحله ببینید خودرو کجاست و چه زمانی به دست شما می‌رسد."
      />

      {query.isPending ? (
        <div className="grid gap-4">
          <Skeleton className="h-80 rounded-xl" />
          <Skeleton className="h-80 rounded-xl" />
        </div>
      ) : query.isError ? (
        <PanelQueryError title="سفارش‌ها بارگیری نشد" query={query} />
      ) : (
        <div className="grid gap-8">
          <SaleSchedules />
          <OrderSections orders={query.data.orders} />
        </div>
      )}
    </div>
  );
}

type Sale = PanelOverview["sales"][number];

const dueStatusTone = {
  paid: "bg-emerald-500/10 text-emerald-700 dark:text-emerald-400",
  partial: "bg-amber-500/10 text-amber-800 dark:text-amber-400",
  open: "",
  overdue: "bg-destructive/10 text-destructive",
} satisfies Record<DueStatus, string>;

/**
 * برنامه پرداخت مانده هر خرید خودرو که هنگام فروش توافق شد؛ از همان overview سایدبار خوانده
 * می‌شود، پس درخواست تازه‌ای نمی‌سازد. پرداخت‌ها به ترتیب موعد از قدیم‌ترین مرحله کم می‌شود.
 */
function SaleSchedules() {
  const { data: sales = [] } = useQuery(
    orpc.panel.overview.queryOptions({ select: (data) => data.sales }),
  );
  if (sales.length === 0) return null;
  return (
    <section className="grid gap-4" aria-labelledby="sale-schedules">
      <h2 id="sale-schedules" className="text-lg font-black">
        برنامه پرداخت خرید
      </h2>
      {sales.map((sale, index) => (
        <SaleSchedule key={sale.dues[0]?.id ?? sale.loan?.planId ?? index} sale={sale} />
      ))}
    </section>
  );
}

function SaleSchedule({ sale }: { sale: Sale }) {
  return (
    <Card>
      <CardContent className="grid gap-4">
        <div className="flex flex-wrap items-start justify-between gap-2">
          <div className="grid gap-1">
            <p className="font-black">{sale.vehicle || "خرید خودرو"}</p>
            <p className="text-sm text-muted-foreground">{saleTermModeLabels[sale.mode]}</p>
          </div>
          <div className="grid gap-1 text-end">
            <p className="text-xs text-muted-foreground">مانده نقدی</p>
            <p className="font-black tabular-nums">
              {sale.balance > 0 ? faToman(sale.balance) : "تسویه شده"}
            </p>
          </div>
        </div>
        {sale.dues.length > 0 ? (
          <ul className="grid divide-y rounded-lg border">
            {sale.dues.map((due) => (
              <li key={due.id} className="flex flex-wrap items-center justify-between gap-2 p-3">
                <span className="grid gap-0.5">
                  <span className="text-sm font-bold">{due.title}</span>
                  <span className="text-xs text-muted-foreground tabular-nums">
                    موعد {faDate(due.dueIso)}
                    {SEP}
                    {faToman(due.amount)}
                    {due.paid > 0 && due.left > 0
                      ? `${SEP}پرداخت‌شده ${faToman(due.paid)}${SEP}مانده ${faToman(due.left)}`
                      : ""}
                    {due.status === "overdue" ? `${SEP}${faNumber(due.daysLate)} روز گذشته` : ""}
                  </span>
                </span>
                <Badge variant="outline" className={dueStatusTone[due.status]}>
                  {dueStatusLabels[due.status]}
                </Badge>
              </li>
            ))}
          </ul>
        ) : null}
        {sale.loan ? (
          <p className="text-sm">
            <Link to="/panel/installments" className="font-bold text-primary hover:underline">
              وام اقساطی {faToman(sale.loan.principal)}
            </Link>
            {SEP}
            {faNumber(sale.loan.paidMonths)} از {faNumber(sale.loan.totalMonths)} قسط پرداخت‌شده
            {SEP}
            قسط ماهانه {faToman(sale.loan.monthlyAmount)} با سود {faNumber(sale.loan.annualRate)}٪
            {sale.loan.nextDueIso ? `${SEP}قسط بعدی ${faDate(sale.loan.nextDueIso)}` : ""}
          </p>
        ) : null}
        {sale.balance > 0 ? (
          <p className="text-xs text-muted-foreground">
            برای پرداخت هر مرحله با کارشناس فروش یا صندوق شعبه هماهنگ کنید.
          </p>
        ) : null}
      </CardContent>
    </Card>
  );
}

/** Orders on the way first; delivered ones below under their own heading */
function OrderSections({ orders }: { orders: TrackedOrder[] }) {
  // a delivered car whose minutes wait for the signature is not done yet: it stays on top
  const open = (order: TrackedOrder) =>
    order.deliveredIso === null || order.deliveryMinutes?.signedIso === null;
  const active = orders.filter(open);
  const delivered = orders.filter((order) => !open(order));
  return (
    <div className="grid gap-8">
      {active.length === 0 ? (
        <PanelEmpty
          icon={DeliveryTruck01Icon}
          title="سفارش در جریانی ندارید"
          description="پس از ثبت سفارش، مراحل تأمین، حمل، گمرک، ترخیص و شماره‌گذاری خودرو همین‌جا قابل پیگیری است."
        />
      ) : (
        <div className="grid gap-5">
          {active.map((order) => (
            <OrderCard key={order.id} order={order} />
          ))}
        </div>
      )}
      {delivered.length > 0 ? (
        <section className="grid gap-5" aria-labelledby="delivered-orders">
          <h2 id="delivered-orders" className="text-lg font-black">
            تحویل‌شده
          </h2>
          {delivered.map((order) => (
            <OrderCard key={order.id} order={order} />
          ))}
        </section>
      ) : null}
    </div>
  );
}

type TimelineKind = TrackedOrder["timeline"][number]["kind"];

/** هر نوع رویداد با آیکن و رنگ خودش؛ توقف مثل مرحله انجام‌شده تیک سبز نمی‌گیرد */
const eventLooks = {
  stage: {
    icon: CheckmarkCircle02Icon,
    tone: "bg-emerald-500/15 text-emerald-700 dark:text-emerald-400",
  },
  hold: { icon: PauseIcon, tone: "bg-amber-500/15 text-amber-800 dark:text-amber-400" },
  resume: { icon: PlayIcon, tone: "bg-sky-500/15 text-sky-700 dark:text-sky-400" },
  date: { icon: Calendar03Icon, tone: "bg-amber-500/15 text-amber-800 dark:text-amber-400" },
  payment: {
    icon: MoneyReceive02Icon,
    tone: "bg-emerald-500/10 text-emerald-700 dark:text-emerald-400",
  },
} satisfies Record<TimelineKind, { icon: typeof CheckmarkCircle02Icon; tone: string }>;

const eventTitles = {
  hold: "توقف موقت",
  resume: "ادامه فرآیند",
  date: "تغییر زمان‌بندی",
  payment: "پرداخت",
} satisfies Record<Exclude<TimelineKind, "stage">, string>;

function OrderCard({ order }: { order: TrackedOrder }) {
  const substepTitle = order.substeps.find((item) => item.current)?.substep.title ?? "";
  const currentLabel = order.hold
    ? order.hold
    : substepTitle
      ? `${orderStageLabels[order.stage]} – ${substepTitle}`
      : orderStageLabels[order.stage];

  return (
    <Card id={`order-${order.id}`} className="scroll-mt-24">
      <CardContent className="grid gap-5">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="flex items-center gap-3">
            <Image
              src={order.vehicle.image}
              alt={order.vehicle.name}
              width={96}
              height={64}
              className="h-16 w-24 shrink-0 rounded-lg object-cover"
            />
            <div className="grid gap-0.5">
              <span className="font-black">
                {order.vehicle.name}{" "}
                <span className="font-normal text-muted-foreground">
                  مدل {faPlainNumber(order.modelYear)}
                  {SEP}
                  {order.color}
                </span>
              </span>
              <span className="text-xs text-muted-foreground">شماره سفارش {order.orderNumber}</span>
              {order.vin ? (
                <span dir="ltr" className="text-start font-mono text-xs text-muted-foreground">
                  VIN: {order.vin}
                </span>
              ) : null}
            </div>
          </div>
          <div className="flex flex-wrap gap-1.5">
            <Badge
              className={
                order.hold
                  ? "bg-amber-500/15 text-amber-800 dark:text-amber-400"
                  : "bg-primary/10 text-primary"
              }
            >
              {order.hold ? "توقف موقت فرآیند" : orderStageLabels[order.stage]}
            </Badge>
            {order.delayed ? <Badge variant="destructive">با تأخیر</Badge> : null}
          </div>
        </div>

        <StageBar order={order} />

        {order.deliveryMinutes ? (
          <DeliveryMinutesCard order={order} minutes={order.deliveryMinutes} />
        ) : null}

        {order.hold ? (
          <Alert>
            <HugeiconsIcon icon={Alert01Icon} strokeWidth={1.8} />
            <AlertTitle>توقف موقت در مرحله {orderStageLabels[order.stage]}</AlertTitle>
            <AlertDescription>
              {order.hold}؛ به‌محض رفع، مرحله بعدی همین‌جا به‌روزرسانی می‌شود.
            </AlertDescription>
          </Alert>
        ) : null}

        <dl className="grid gap-x-6 gap-y-2 rounded-xl border bg-muted/40 p-4 text-sm sm:grid-cols-2">
          <Row label="وضعیت فعلی" value={<span className="font-bold">{currentLabel}</span>} />
          <Row label="نوع پلاک" value={order.plateKindLabel} />
          <Row label="آخرین بروزرسانی" value={faDateTimeShort(order.updatedIso)} />
          <Row label="موقعیت" value={order.location} />
          <Row label="شناور" value={order.vessel} />
          <Row label="تاریخ حرکت" value={order.departureIso ? faDate(order.departureIso) : null} />
          <Row
            label="زمان تقریبی رسیدن به کیش"
            value={order.arrivalEtaIso ? faDate(order.arrivalEtaIso) : null}
          />
          <Row
            label={order.deliveredIso ? "تحویل شده در" : "تحویل تقریبی"}
            value={
              order.deliveredIso ? (
                <span className="font-bold">{faDate(order.deliveredIso)}</span>
              ) : (
                <span className="font-bold">
                  {faDate(order.estimatedDeliveryIso)}
                  {order.daysToDelivery > 0 ? ` (${faNumber(order.daysToDelivery)} روز دیگر)` : ""}
                  {order.delayed && order.daysToDelivery < 0 ? (
                    <span className="block text-xs font-normal text-muted-foreground">
                      این تاریخ گذشته است؛ زمان جدید پس از بررسی همین‌جا اعلام می‌شود.
                    </span>
                  ) : null}
                </span>
              )
            }
          />
        </dl>

        {order.substeps.length > 0 ? (
          <section className="grid gap-2">
            <h3 className="text-sm font-bold">ریزمراحل {orderStageLabels[order.stage]}</h3>
            <ul className="grid gap-1.5 text-sm sm:grid-cols-2">
              {order.substeps.map(({ substep, done, current }) => (
                <li
                  key={substep.id}
                  className={cn(
                    "flex items-center gap-2",
                    current
                      ? "font-bold"
                      : done
                        ? "text-muted-foreground"
                        : "text-muted-foreground/70",
                  )}
                >
                  {done ? (
                    <HugeiconsIcon
                      icon={CheckmarkCircle02Icon}
                      strokeWidth={2}
                      className="size-4 shrink-0 text-emerald-600 dark:text-emerald-400"
                    />
                  ) : (
                    <span
                      className={cn(
                        "size-2 shrink-0 rounded-full",
                        current ? "bg-primary ring-4 ring-primary/20" : "bg-border",
                      )}
                      aria-hidden
                    />
                  )}
                  <span aria-current={current ? "step" : undefined}>
                    {substep.title}
                    <span className="sr-only">
                      {done ? "، انجام شد" : current ? "، مرحله فعلی" : ""}
                    </span>
                  </span>
                </li>
              ))}
            </ul>
          </section>
        ) : null}

        <div className="grid gap-1.5">
          <div className="flex flex-wrap items-center justify-between gap-2 text-sm">
            <span className="text-muted-foreground">وضعیت پرداخت</span>
            <span className="font-bold">
              {faToman(order.paidAmount + (order.tradeIn?.credit ?? 0) + order.loanCredit)} از{" "}
              {faToman(order.totalPrice)}
            </span>
          </div>
          <Progress
            value={Math.min(
              100,
              Math.round(
                ((order.paidAmount + (order.tradeIn?.credit ?? 0) + order.loanCredit) /
                  Math.max(1, order.totalPrice)) *
                  100,
              ),
            )}
            aria-label="درصد تسویه‌شده سفارش"
          />
          {order.tradeIn ? (
            <p className="text-xs text-muted-foreground">
              شامل ارزش خودروی معاوضه ({order.tradeIn.vehicle}): {faToman(order.tradeIn.credit)}
            </p>
          ) : null}
          {order.loanCredit > 0 ? (
            <p className="text-xs text-muted-foreground">
              شامل وام اقساطی: {faToman(order.loanCredit)}؛ بازپرداخت آن در{" "}
              <Link to="/panel/installments" className="font-bold text-foreground underline">
                صفحه اقساط
              </Link>{" "}
              انجام می‌شود.
            </p>
          ) : null}
          {order.balance > 0 ? (
            <div className="flex flex-wrap items-center justify-between gap-2 pt-1">
              <span className="text-sm text-muted-foreground">مانده {faToman(order.balance)}</span>
              <PayDialog
                purpose="vehicle_order"
                targetId={order.id}
                title={`سفارش ${order.orderNumber}`}
              >
                {order.paidAmount > 0 ? "پرداخت مانده" : "پرداخت بیعانه"}
              </PayDialog>
            </div>
          ) : null}
        </div>

        <TargetPayments purpose="vehicle_order" targetId={order.id} />

        <section className="grid gap-3 rounded-xl border p-4" aria-label="اسناد و مدارک سفارش">
          <h3 className="flex items-center gap-2 text-sm font-bold">
            <HugeiconsIcon icon={Folder01Icon} strokeWidth={1.8} className="size-4" />
            اسناد و مدارک سفارش
            {order.files.length > 0 ? (
              <span className="font-normal text-muted-foreground">
                ({faNumber(order.files.length)} سند)
              </span>
            ) : null}
          </h3>
          <OrderDocuments
            orderId={order.id}
            files={order.files}
            emptyText="هنوز سندی برای این سفارش بارگذاری نشده است؛ اسناد هر مرحله پس از صدور همین‌جا قرار می‌گیرد."
          />
        </section>

        <Accordion>
          <AccordionItem value="timeline" className="border-b-0">
            <AccordionTrigger className="py-2 hover:no-underline">
              <span className="font-bold">تاریخچه مراحل سفارش</span>
            </AccordionTrigger>
            <AccordionContent className="pb-2">
              <ol className="mt-2">
                {[...order.timeline].reverse().map((event, index) => (
                  <li key={event.id} className="relative flex gap-3 pb-4 last:pb-0">
                    <span className="flex flex-col items-center">
                      <span
                        className={cn(
                          "grid size-6 shrink-0 place-items-center rounded-full",
                          index === 0 && event.kind === "stage"
                            ? "bg-primary text-primary-foreground"
                            : eventLooks[event.kind].tone,
                        )}
                      >
                        <HugeiconsIcon
                          icon={eventLooks[event.kind].icon}
                          strokeWidth={2}
                          className="size-4"
                        />
                      </span>
                      {index < order.timeline.length - 1 ? (
                        <span className="w-px flex-1 bg-border" />
                      ) : null}
                    </span>
                    <div className="grid gap-0.5 pb-1">
                      <span className="text-sm font-bold">
                        {event.kind === "stage"
                          ? orderStageLabels[event.stage]
                          : `${eventTitles[event.kind]}${SEP}${orderStageLabels[event.stage]}`}
                      </span>
                      <span className="text-xs text-muted-foreground">
                        {faDateTimeShort(event.dateIso)}
                      </span>
                      <span className="text-sm text-muted-foreground">{event.note}</span>
                    </div>
                  </li>
                ))}
              </ol>
            </AccordionContent>
          </AccordionItem>
        </Accordion>
      </CardContent>
    </Card>
  );
}

/** نوار مراحل اصلی؛ روی موبایل به‌صورت افقی اسکرول می‌شود. */
function StageBar({ order }: { order: TrackedOrder }) {
  const lastIndex = orderStageKeys.length - 1;
  const documentCount = (stage: TrackedOrder["stage"]) =>
    order.files.filter((file) => file.stage === stage).length;
  const scroller = useRef<HTMLDivElement>(null);
  const currentStep = useRef<HTMLLIElement>(null);
  // on a narrow screen the current stage sits far along the strip: centre it in the strip only
  // (scrollIntoView would also scroll the page, fighting the #order-… landing)
  useEffect(() => {
    const box = scroller.current;
    const step = currentStep.current;
    if (!box || !step) return;
    const boxRect = box.getBoundingClientRect();
    const stepRect = step.getBoundingClientRect();
    box.scrollBy({
      left: stepRect.left + stepRect.width / 2 - (boxRect.left + boxRect.width / 2),
    });
  }, [order.stageIndex]);

  return (
    <div className="grid gap-2">
      <div className="flex items-center justify-between text-xs text-muted-foreground">
        <span>
          مرحله {faNumber(order.stageIndex + 1)} از {faNumber(orderStageKeys.length)}
        </span>
        <span>{faPercent(Math.round(order.progress * 100))}</span>
      </div>
      <div ref={scroller} className="-mx-1.5 -my-1.5 overflow-x-auto px-1.5 py-1.5">
        <ol className="flex min-w-max items-start" aria-label="مراحل سفارش">
          {orderStageKeys.map((stage, index) => {
            const done = index < order.stageIndex;
            const current = index === order.stageIndex;
            const documents = documentCount(stage);
            return (
              <li
                key={stage}
                ref={current ? currentStep : undefined}
                aria-current={current ? "step" : undefined}
                className="flex w-[4.75rem] shrink-0 flex-col items-center gap-1.5"
              >
                <div className="flex w-full items-center">
                  <Connector filled={done || current} hidden={index === 0} />
                  <span
                    className={cn(
                      "grid size-6 shrink-0 place-items-center rounded-full border",
                      done &&
                        "border-transparent bg-emerald-500/15 text-emerald-700 dark:text-emerald-400",
                      current &&
                        (order.hold
                          ? "border-transparent bg-amber-500 text-white ring-4 ring-amber-500/20"
                          : "border-transparent bg-primary text-primary-foreground ring-4 ring-primary/20"),
                      !done && !current && "border-dashed bg-background",
                    )}
                    aria-hidden
                  >
                    {done ? (
                      <HugeiconsIcon
                        icon={CheckmarkCircle02Icon}
                        strokeWidth={2}
                        className="size-4"
                      />
                    ) : current ? (
                      <span className="size-2 rounded-full bg-current" />
                    ) : null}
                  </span>
                  <Connector filled={done} hidden={index === lastIndex} />
                </div>
                <span
                  className={cn(
                    "text-center text-xs leading-4",
                    current ? "font-bold" : "text-muted-foreground",
                  )}
                >
                  {orderStageLabels[stage]}
                  {/* the icon above is decorative: its meaning read as text */}
                  <span className="sr-only">
                    {done
                      ? "، انجام شد"
                      : current
                        ? order.hold
                          ? "، مرحله فعلی، متوقف"
                          : "، مرحله فعلی"
                        : "، در پیش"}
                  </span>
                </span>
                {documents > 0 ? (
                  <span className="inline-flex items-center gap-0.5 rounded-full bg-muted px-1.5 text-xs leading-4 text-muted-foreground">
                    <HugeiconsIcon
                      icon={Attachment01Icon}
                      strokeWidth={2}
                      className="size-3"
                      aria-hidden
                    />
                    {faNumber(documents)}
                    <span className="sr-only"> سند</span>
                  </span>
                ) : null}
              </li>
            );
          })}
        </ol>
      </div>
    </div>
  );
}

function Connector({ filled, hidden }: { filled: boolean; hidden: boolean }) {
  return (
    <span
      className={cn(
        "h-0.5 flex-1",
        hidden ? "bg-transparent" : filled ? "bg-primary" : "bg-border",
      )}
      aria-hidden
    />
  );
}

function Row({ label, value }: { label: string; value: ReactNode }) {
  if (value === null || value === "") return null;
  return (
    <div className="flex items-baseline justify-between gap-3 border-b border-dashed pb-1.5 last:border-0 sm:border-0 sm:pb-0">
      <dt className="text-muted-foreground">{label}</dt>
      <dd className="text-end">{value}</dd>
    </div>
  );
}
